import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Check, X, CheckCircle } from '@phosphor-icons/react';
import { API } from './adminConstants';
import { CsvExportButton } from './CsvHelpers';

export function HolidayAdmin() {
  const { getAuthHeader } = useAuth();
  const [subTab, setSubTab] = useState('approvals');
  const [pending, setPending] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [note, setNote] = useState('');
  const [noteModal, setNoteModal] = useState(null);

  const load = async () => {
    const headers = getAuthHeader();
    const [pRes, aRes, tRes] = await Promise.all([
      axios.get(`${API}/holiday/team/requests?status_filter=pending`, { headers }),
      axios.get(`${API}/holiday/team/requests`, { headers }),
      axios.get(`${API}/holiday/team/dashboard`, { headers }),
    ]);
    setPending(pRes.data.filter(r => !r.cancellation_requested));
    setCancellations(aRes.data.filter(r => r.cancellation_requested && r.status !== 'cancelled'));
    setAllRequests(aRes.data);
    setTeamData(tRes.data);
  };
  useEffect(() => { load(); }, []);

  const handleAction = async (id, action) => {
    try {
      await axios.put(`${API}/holiday/team/requests/${id}/${action}?admin_notes=${encodeURIComponent(note)}`, {}, { headers: getAuthHeader() });
      toast.success(`Request ${action}d`);
      setNoteModal(null); setNote(''); load();
    } catch { toast.error('Failed'); }
  };

  const handleCancelAction = async (id, approve) => {
    try {
      const endpoint = approve ? 'approve-cancel' : 'reject-cancel';
      await axios.put(`${API}/requests/${id}/${endpoint}`, {}, { headers: getAuthHeader() });
      toast.success(approve ? 'Cancellation approved' : 'Cancellation rejected');
      load();
    } catch { toast.error('Failed'); }
  };

  const subTabs = [
    { id: 'approvals', label: 'Approvals', count: pending.length + cancellations.length },
    { id: 'overview', label: 'Team Overview' },
    { id: 'allowances', label: 'Allowances' },
  ];

  return (
    <div data-testid="holiday-admin">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Holiday Administration</h1></div>
        <div className="flex gap-2">
          <CsvExportButton endpoint="/csv/export/holidays" label="Export Holidays" />
          <CsvExportButton endpoint="/csv/backup/holidays" label="Full Backup" />
        </div>
      </div>
      <div className="flex gap-2 mb-6">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === t.id ? 'bg-lime-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`} data-testid={`holiday-subtab-${t.id}`}>
            {t.label}{t.count > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-white/20">{t.count}</span>}
          </button>
        ))}
      </div>

      {subTab === 'approvals' && (
        <div className="space-y-4">
          {pending.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="p-4 border-b border-slate-100"><h2 className="font-semibold text-slate-900">Pending Requests ({pending.length})</h2></div>
              <div className="divide-y divide-slate-100">
                {pending.map(req => (
                  <div key={req.id} className="p-4 flex items-center justify-between" data-testid={`admin-pending-${req.id}`}>
                    <div>
                      <p className="font-medium text-slate-900">{req.submitted_by_name}</p>
                      <p className="text-sm text-slate-500">{req.start_date && new Date(req.start_date).toLocaleDateString('en-GB')} - {req.end_date && new Date(req.end_date).toLocaleDateString('en-GB')}{req.working_days ? ` (${req.working_days} day${req.working_days !== 1 ? 's' : ''})` : ''}{req.day_type && req.day_type !== 'full_day' ? ` [${req.day_type.toUpperCase()}]` : ''}</p>
                      {req.description && <p className="text-xs text-slate-400 mt-0.5">{req.description}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleAction(req.id, 'approve')} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} className="mr-1" />Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => setNoteModal({ id: req.id, action: 'reject' })} className="border-red-300 text-red-600 hover:bg-red-50"><X size={14} className="mr-1" />Reject</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {cancellations.length > 0 && (
            <div className="bg-white rounded-xl border border-amber-200">
              <div className="p-4 border-b border-amber-100"><h2 className="font-semibold text-amber-800">Cancellation Requests ({cancellations.length})</h2></div>
              <div className="divide-y divide-amber-100">
                {cancellations.map(req => (
                  <div key={req.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{req.submitted_by_name}</p>
                      <p className="text-sm text-slate-500">{req.start_date && new Date(req.start_date).toLocaleDateString('en-GB')} - {req.end_date && new Date(req.end_date).toLocaleDateString('en-GB')}</p>
                      <p className="text-xs text-amber-600 mt-0.5">{req.cancellation_reason}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleCancelAction(req.id, true)} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} className="mr-1" />Approve Cancel</Button>
                      <Button size="sm" variant="outline" onClick={() => handleCancelAction(req.id, false)} className="border-red-300 text-red-600"><X size={14} className="mr-1" />Reject Cancel</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {pending.length === 0 && cancellations.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-12 text-center"><CheckCircle size={40} className="mx-auto text-emerald-400 mb-2" /><p className="text-slate-600">No pending requests</p></div>}
        </div>
      )}

      {subTab === 'overview' && teamData && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Team Overview ({teamData.members?.length || 0} members)</h2>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Job Title</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Department</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Total</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Used</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Remaining</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Usage</th>
              </tr>
            </thead>
            <tbody>
              {(teamData.members || []).map(m => (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{m.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{m.job_title || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{m.department || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{m.total_allowance}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{m.days_used}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{m.days_remaining}</td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-lime-500 rounded-full" style={{ width: `${m.usage_percent}%` }} /></div><span className="text-xs text-slate-500 w-10 text-right">{m.usage_percent}%</span></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subTab === 'allowances' && teamData && (
        <AllowancesTab members={teamData.members || []} reload={load} />
      )}

      {noteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Rejection Reason</h3>
            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note (optional)..." rows={3} />
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => { setNoteModal(null); setNote(''); }} className="flex-1">Cancel</Button>
              <Button onClick={() => handleAction(noteModal.id, noteModal.action)} className="flex-1 bg-red-600 hover:bg-red-700">Reject</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AllowancesTab({ members, reload }) {
  const { getAuthHeader } = useAuth();
  const handleUpdateAllowance = async (userId, value) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/allowance?allowance=${value}`, {}, { headers: getAuthHeader() });
      toast.success('Allowance updated');
      reload();
    } catch { toast.error('Failed'); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-4 border-b border-slate-100"><h2 className="font-semibold text-slate-900">Holiday Allowances</h2></div>
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Role</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Department</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Allowance (days)</th>
          </tr>
        </thead>
        <tbody>
          {members.map(m => (
            <tr key={m.id} className="border-b border-slate-100">
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{m.name}</td>
              <td className="px-4 py-3 text-sm text-slate-600 capitalize">{m.role}</td>
              <td className="px-4 py-3 text-sm text-slate-600">{m.department || '-'}</td>
              <td className="px-4 py-3"><Input type="number" className="h-8 w-20 text-sm border-slate-200" defaultValue={m.total_allowance} onBlur={e => { const v = parseInt(e.target.value); if (v > 0) handleUpdateAllowance(m.id, v); }} min="0" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
